import { useMemo, useState } from "react"
import { Box, Chip, Stack, Typography } from "@mui/material"
import {
    DndContext,
    DragOverlay,
    MeasuringStrategy,
    PointerSensor,
    pointerWithin,
    useSensor,
    useSensors,
} from "@dnd-kit/core"
import { Bound } from "lib/@components/binding/Bound"
import { If } from "lib/@components/switch"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { buildAssignmentBoard, shortFolderName } from "./build-assignment-board"
import {
    clearedSelection,
    clickSchedule,
    dragTargets,
    EMPTY_SELECTION,
    folderTakeovers,
    restoreSelection,
    selectionSize,
    selectionTargets,
    toggleAll,
    toggleGroup,
} from "./board-selection"
import { ASSIGNMENT_SAVE_ID, useOptimisticAssignments } from "./use-optimistic-assignments"
import { confirmAndRemoveTrade } from "../confirm-and-remove-trade"
import { countConditionalRulesForTrade } from "../count-conditional-rules-for-trade"
import { UnassignedSchedulesPane } from "./unassigned-schedules-pane"
import { TradeBucketsPane } from "./trade-buckets-pane"
import { ScheduleTasksDrawer } from "./schedule-tasks-drawer"
import { ASSIGNMENT_SEARCH_PARAM } from "./assignment-search-param"
import { describeAssignmentProgress } from "./describe-assignment-progress"
import { describeSelectionScope } from "./describe-selection-scope"
import { BoardLoadError } from "./board-load-error"
import { useRegimeAssignmentData } from "./use-regime-assignment-data"
import { SaveIndicator } from "lib/@components/save-indicator/save-indicator"
import { ASSIGNMENT_KIND_NAMES } from "./assignment-kind-names"
import { UndoSnackbarAction } from "./undo-snackbar-action"

const DROPPABLE_MEASURING = { droppable: { strategy: MeasuringStrategy.Always } }

/**
 * The two-pane assignment board for one regime (FIQ-2457): schedules not yet assigned
 * on the left, grouped by library folder; the skill map's trades on the right. Select
 * then click a trade, or drag onto one. Children read everything through Bound context.
 */
export function RegimeAssignmentBoard({ regimeId, skillMapId, trades, lines, readOnly, onAddTrade, onEditTrade }) {
    const {
        summary,
        assignments: savedAssignments,
        regimeFailed,
        assignmentsFailed,
        refreshFailed,
        retry,
    } = useRegimeAssignmentData({
        regimeId,
        skillMapId,
    })
    const assignmentsLoaded = Array.isArray(savedAssignments)
    // No full-page spinner while saving: changes show at once and roll back if a save fails
    const { assignments, assign, remove } = useOptimisticAssignments({
        skillMapId,
        assignments: assignmentsLoaded ? savedAssignments : undefined,
    })
    const [search] = useSearchParam(ASSIGNMENT_SEARCH_PARAM, "")
    const searching = !!search.trim()
    const [selection, setSelection] = useState(EMPTY_SELECTION)
    const [lastClickedId, setLastClickedId] = useState(null)
    const [openGroupKeys, setOpenGroupKeys] = useState(() => new Set())
    const [openScheduleId, setOpenScheduleId] = useState(null)
    const [dragging, setDragging] = useState(null)
    // Pointer only. A short travel threshold keeps clicks (ticks, opening a schedule) working
    // on draggable rows. There is no keyboard drag: with it, Space and Enter on a row's tick
    // box, title or chevron started a drag that could never drop. Keyboard users tick rows and
    // use "Assign to" instead.
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
    const board = useMemo(build, [summary, assignments, lines, trades, search])
    const selectedCount = selectionSize(selection, board.groups)
    // Schedules another folder's assignment routes that the ticked folders would take over
    const takeoverCount = useMemo(countTakeovers, [board, selection, searching])
    // What assigning the ticked work would cover and which conditional skill rules it overrides
    const selectionScope = useMemo(describeSelection, [board, selection, searching])

    if (regimeFailed) {
        return (
            <BoardLoadError
                message="Couldn't load this regime's schedules."
                onRetry={retry}
                data-cy="assignment-regime-error"
            />
        )
    }
    if (assignmentsFailed) {
        return (
            <BoardLoadError
                message="Couldn't load this skill map's schedule assignments."
                onRetry={retry}
                data-cy="assignment-assignments-error"
            />
        )
    }
    // Until both have arrived every schedule would look unassigned, and could be assigned again
    if (!summary || !assignmentsLoaded) return <LoadingScreen description="Loading the regime's schedules" />

    return (
        <Bound
            board={board}
            trades={trades}
            selection={selection}
            selectedCount={selectedCount}
            takeoverCount={takeoverCount}
            selectionScope={selectionScope}
            searching={searching}
            readOnly={readOnly}
            onToggleAll={handleToggleAll}
            onToggleGroup={handleToggleGroup}
            onToggleSchedule={handleToggleSchedule}
            isGroupOpen={isGroupOpen}
            onToggleGroupOpen={toggleGroupOpen}
            onClearSelection={clearSelection}
            onAssignSelection={assignSelection}
            onAssignTargets={assignTargets}
            onRemoveAssignment={removeAssignment}
            onOpenSchedule={setOpenScheduleId}
            onAddTrade={onAddTrade}
            onEditTrade={onEditTrade}
            onRemoveTrade={removeTrade}
        >
            <DndContext
                sensors={sensors}
                // The trades pane scrolls on its own, so keep re-measuring drop targets during a
                // drag and drop onto whatever is under the pointer
                measuring={DROPPABLE_MEASURING}
                collisionDetection={pointerWithin}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={endDrag}
            >
                <Stack spacing={1.5}>
                    <SaveIndicator id={ASSIGNMENT_SAVE_ID} />
                    <If value={refreshFailed}>
                        <BoardLoadError
                            severity="warning"
                            message="Couldn't refresh the board. It shows what was loaded before."
                            onRetry={retry}
                            data-cy="assignment-refresh-error"
                        />
                    </If>
                    <Typography variant="body2" color="text.secondary" data-cy="assignment-progress">
                        {describeAssignmentProgress(board.progress)}
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 3fr) minmax(0, 2fr)" },
                            gap: 2,
                            alignItems: "start",
                        }}
                    >
                        <UnassignedSchedulesPane />
                        <TradeBucketsPane />
                    </Box>
                </Stack>
                <DragOverlay>
                    <If value={!!dragging}>
                        <Chip label={dragging?.label} color="primary" data-cy="assignment-drag-preview" />
                    </If>
                </DragOverlay>
            </DndContext>
            <ScheduleTasksDrawer scheduleId={openScheduleId} onClose={closeScheduleDrawer} />
        </Bound>
    )

    function build() {
        return buildAssignmentBoard({
            schedules: summary?.schedules ?? [],
            assignments,
            lines: lines ?? [],
            trades,
            search,
        })
    }

    function countTakeovers() {
        return folderTakeovers(board, selectionTargets(selection, board.groups, { searching })).length
    }

    function describeSelection() {
        return describeSelectionScope(selectionTargets(selection, board.groups, { searching }), board)
    }

    function removeTrade(trade) {
        if (readOnly) return
        confirmAndRemoveTrade({
            skillMapId,
            tradeId: trade.tradeId,
            tradeName: trade.tradeName,
            skillCount: trade.skillCount,
            assignmentCount: trade.chips.length,
            // Removing the trade also drops conditional rules that route to it and every rule on
            // its own skills, so the warning counts the skills that lose them
            conditionalRuleCount: countConditionalRulesForTrade(lines, trade.tradeId),
        })
    }

    function handleToggleAll() {
        setSelection(toggleAll(selection, board.groups, { searching, allGroups: board.allGroups }))
    }

    // All groups, not only those a search shows, so unticking keeps hidden covered schedules
    function handleToggleGroup(group) {
        setSelection(toggleGroup(selection, group, { searching, groups: board.allGroups }))
    }

    function handleToggleSchedule(group, scheduleId, shiftKey) {
        setSelection(
            clickSchedule(selection, board, { group, scheduleId, shiftKey, anchorId: lastClickedId, isGroupOpen })
        )
        setLastClickedId(scheduleId)
    }

    function isGroupOpen(group) {
        return searching || openGroupKeys.has(group.key)
    }

    function toggleGroupOpen(group) {
        setOpenGroupKeys((current) => {
            const next = new Set(current)
            if (next.has(group.key)) next.delete(group.key)
            else next.add(group.key)
            return next
        })
    }

    function clearSelection() {
        setSelection(EMPTY_SELECTION)
    }

    function assignSelection(tradeId) {
        return assignTargets(tradeId, selectionTargets(selection, board.groups, { searching }))
    }

    // Moving a chip between trades, or overriding one task in the drawer, leaves the
    // left-hand ticks alone; only assigning the ticked work clears them, and a failed save
    // puts them back so the user can try again
    async function assignTargets(tradeId, targets, { moving = false, keepSelection = false } = {}) {
        if (readOnly || !targets.length) return
        const clearing = !moving && !keepSelection
        const cleared = clearedSelection()
        const previous = selection
        if (clearing) setSelection(cleared)
        if (await assign(tradeId, targets)) {
            successSnackbar(`${describeTargets(targets)} ${moving ? "moved" : "assigned"} to ${trades[tradeId]}`)
        } else if (clearing) {
            setSelection(restoreSelection(previous, cleared))
        }
    }

    // Removing is one click on a small button, and a folder can carry a lot of work, so once
    // the remove has saved the board says what went and offers to put it back
    async function removeAssignment(assignmentId) {
        if (readOnly) return
        const removed = assignments.find(isAssignment)
        if (!(await remove([assignmentId])) || !removed) return
        successSnackbar(`Removed ${describeRemoved(removed)} from ${trades[removed.tradeId]}`, {
            action: undoAction,
            autoHideDuration: 10000,
        })

        function isAssignment(assignment) {
            return assignment.id === assignmentId
        }

        function undoAction(snackbarKey) {
            return <UndoSnackbarAction snackbarKey={snackbarKey} onUndo={undo} />
        }

        // Assigning the same target again restores it, as a fresh write
        function undo() {
            const target = { kind: removed.kind, targetId: removed.targetId, name: removed.name }
            assignTargets(removed.tradeId, [target], { keepSelection: true })
        }
    }

    function handleDragStart({ active }) {
        const payload = active.data.current
        const targets = dragTargetsFor(payload)
        setDragging({ targets, label: describeTargets(targets), fromTradeId: payload?.chip?.tradeId })
    }

    function handleDragEnd({ over }) {
        const tradeId = over?.data.current?.tradeId
        const moving = !!dragging?.fromTradeId
        // Dropping a chip back on its own trade changes nothing
        if (tradeId && tradeId !== dragging?.fromTradeId && dragging?.targets.length) {
            assignTargets(tradeId, dragging.targets, { moving })
        }
        endDrag()
    }

    function endDrag() {
        setDragging(null)
    }

    // A chip on a trade carries its own assignment, so dropping it elsewhere moves it.
    // Anything from the left-hand list carries the selection too (see dragTargets).
    function dragTargetsFor(payload) {
        if (!payload) return []
        const { chip } = payload
        if (chip) return [{ kind: chip.kind, targetId: chip.targetId, name: chip.name || chip.label }]
        return dragTargets(selection, board.groups, payload, { searching })
    }

    function closeScheduleDrawer() {
        setOpenScheduleId(null)
    }
}

function describeTargets(targets) {
    if (targets.length === 1) return targets[0].name
    return `${targets.length} items`
}

// "folder Boilers", "schedule 22-11 Fire alarms", "task 22-11 · Monthly"
function describeRemoved(assignment) {
    const name = assignment.kind === "folder" ? shortFolderName(assignment.name) : assignment.name
    return `${ASSIGNMENT_KIND_NAMES[assignment.kind]?.toLowerCase() ?? "assignment"} ${name || ""}`.trim()
}
